/**
 * Core Tasks Tab (Epic 2 — Kerntakencatalogus, Story 2.2)
 *
 * Catalogusbeheer-UI onder Instellingen > Functieloonhuis > Kerntaken.
 * Tabel gegroepeerd per domein + Sheet (custom rechts-uitschuivend paneel —
 * dit project heeft geen shadcn/ui geïnstalleerd, zie Dev Notes) voor
 * aanmaken/bewerken, + archiveer-bevestigingsdialoog.
 *
 * Kleuren volgen uitsluitend het XXtract-palet (navy #2F5A7A, teal #54949E,
 * groen #B7D945, rood #D64545 — alleen bij echte fouten), conform NFR-1.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useCoreTasks,
  useCreateCoreTask,
  useUpdateCoreTask,
  useArchiveCoreTask,
  useReactivateCoreTask,
} from '../useCoreTasks';
import { competenciesApi, type Competency } from '../../../lib/competenciesApi';
import { matchesSearch } from '../../../lib/textUtils';
import { useClickOutside } from '../../../hooks/useClickOutside';
import type { CoreTaskDTO, CreateCoreTaskRequest, UpdateCoreTaskRequest } from '@functieloonhuis/shared';

const NEW_DOMAIN_SENTINEL = '__new_domain__';

/**
 * Sluit de opgegeven callback bij een Escape-toetsaanslag, zolang `active`
 * waar is (UX-DR8, bindend: "toetsenbord-navigeerbare dialogen"). Gedeeld
 * door de Sheet en de archiveer-dialoog.
 */
function useEscapeToClose(active: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!active) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [active, onClose]);
}

/**
 * Rij-acties-menu (Bewerken/Archiveren) met click-outside-dismissal —
 * epic-level review fix: eerdere versie sloot alleen bij het klikken van een
 * eigen knop, niet bij een klik elders in de pagina.
 */
function RowActionsMenu({
  task,
  onEdit,
  onArchiveRequest,
}: {
  task: CoreTaskDTO;
  onEdit: (task: CoreTaskDTO) => void;
  onArchiveRequest: (task: CoreTaskDTO) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        data-testid={`core-task-row-menu-${task.id}`}
        onClick={() => setOpen((v) => !v)}
        className="text-gray-400 hover:text-gray-600 px-2"
        aria-label="Acties"
      >
        &#8942;
      </button>
      {open && (
        <div className="absolute right-6 top-2 z-20 w-40 rounded-md border border-[#E2E8F0] bg-white shadow-lg py-1 text-left">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onEdit(task);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-[#1E293B] hover:bg-gray-50"
          >
            Bewerken
          </button>
          <button
            type="button"
            data-testid={`core-task-row-archive-${task.id}`}
            onClick={() => {
              setOpen(false);
              onArchiveRequest(task);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-[#1E293B] hover:bg-gray-50"
          >
            {task.isActive ? 'Archiveren' : 'Dearchiveren'}
          </button>
        </div>
      )}
    </div>
  );
}

interface CoreTaskFormData {
  name: string;
  domain: string;
  isNewDomain: boolean;
  newDomain: string;
  skills: string[];
  competencyIds: number[];
}

const emptyFormData: CoreTaskFormData = {
  name: '',
  domain: '',
  isNewDomain: false,
  newDomain: '',
  skills: [''],
  competencyIds: [],
};

function SkillsPopover({ skills }: { skills: CoreTaskDTO['skills'] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        data-testid="core-task-skills-chip"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#54949E]/10 text-[#2F5A7A] hover:bg-[#54949E]/20"
      >
        {skills.length} {skills.length === 1 ? 'vaardigheid' : 'vaardigheden'}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-64 rounded-md border border-[#E2E8F0] bg-white shadow-lg p-2">
          {skills.length > 0 ? (
            <ul className="space-y-1">
              {skills.map((s) => (
                <li key={s.id} className="text-sm text-[#1E293B]">
                  {s.label}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">Geen vaardigheden</p>
          )}
        </div>
      )}
    </div>
  );
}

export function CoreTasksTab() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: coreTasks, isLoading, error } = useCoreTasks(includeArchived);
  const {
    data: vakCompetencies,
    isLoading: isLoadingVakCompetencies,
    error: vakCompetenciesError,
  } = useQuery<Competency[]>({
    queryKey: ['competencies', 'vak', 'active'],
    queryFn: () => competenciesApi.getVak(true),
  });

  const createCoreTask = useCreateCoreTask();
  const updateCoreTask = useUpdateCoreTask();
  const archiveCoreTask = useArchiveCoreTask();
  const reactivateCoreTask = useReactivateCoreTask();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCoreTask, setEditingCoreTask] = useState<CoreTaskDTO | null>(null);
  const [formData, setFormData] = useState<CoreTaskFormData>(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [nameTakenWarning, setNameTakenWarning] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CoreTaskDTO | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEscapeToClose(isFormOpen, () => setIsFormOpen(false));
  useEscapeToClose(!!archiveTarget, () => setArchiveTarget(null));

  const domains = useMemo(() => {
    const seen: string[] = [];
    for (const task of coreTasks ?? []) {
      if (!seen.includes(task.domain)) seen.push(task.domain);
    }
    return seen;
  }, [coreTasks]);

  const filteredTasks = useMemo(() => {
    if (!coreTasks) return [];
    if (!searchQuery) return coreTasks;
    return coreTasks.filter((t) => matchesSearch(t.name, searchQuery) || matchesSearch(t.domain, searchQuery));
  }, [coreTasks, searchQuery]);

  const groupedByDomain = useMemo(() => {
    const groups = new Map<string, CoreTaskDTO[]>();
    for (const task of filteredTasks) {
      const list = groups.get(task.domain) ?? [];
      list.push(task);
      groups.set(task.domain, list);
    }
    // Preserve first-occurrence domain order.
    const orderedDomains: string[] = [];
    for (const task of filteredTasks) {
      if (!orderedDomains.includes(task.domain)) orderedDomains.push(task.domain);
    }
    return orderedDomains.map((domain) => ({ domain, tasks: groups.get(domain) ?? [] }));
  }, [filteredTasks]);

  const activeCount = (coreTasks ?? []).filter((t) => t.isActive).length;

  const resetForm = () => {
    setFormData(emptyFormData);
    setEditingCoreTask(null);
    setFormError(null);
    setNameTakenWarning(null);
    setIsFormOpen(false);
  };

  const handleAdd = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const handleEdit = (task: CoreTaskDTO) => {
    setEditingCoreTask(task);
    setFormData({
      name: task.name,
      domain: task.domain,
      isNewDomain: false,
      newDomain: '',
      skills: task.skills.length > 0 ? task.skills.map((s) => s.label) : [''],
      competencyIds: task.competencies.map((c) => c.id),
    });
    setFormError(null);
    setNameTakenWarning(null);
    setIsFormOpen(true);
  };

  /**
   * Zoekt een naam-botsing tegen de al-geladen actieve-kerntaken-lijst
   * (client-side UX-verbetering, geen vervanging van de server-side check
   * uit Story 2.1). Pure functie i.p.v. alleen een onBlur-handler zodat
   * `handleSubmit` dezelfde check ook bij Enter-submit (die onBlur kan
   * overslaan) opnieuw kan uitvoeren — epic-level review fix.
   */
  const findNameClash = (name: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const clash = (coreTasks ?? []).find(
      (t) => t.isActive && t.name === trimmed && t.id !== editingCoreTask?.id
    );
    return clash ? 'Er bestaat al een actieve kerntaak met deze naam.' : null;
  };

  const checkNameTaken = (name: string) => {
    setNameTakenWarning(findNameClash(name));
  };

  const effectiveDomain = formData.isNewDomain ? formData.newDomain.trim() : formData.domain;
  const trimmedSkills = formData.skills.map((s) => s.trim()).filter((s) => s.length > 0);

  /**
   * Epic-level review fix: een kerntaak kan een competentie hebben die NA
   * het aanmaken elders (Instellingen > Competenties) gearchiveerd is.
   * `competenciesApi.getVak(true)` (actief-only) bevat die competentie dan
   * niet meer, dus de checkbox-lijst kan 'm niet tonen/uitvinken — zonder
   * deze afhandeling zou elke bewerking (ook alleen een naam- of
   * vaardigheid-wijziging) de nog-steeds-meegestuurde, inmiddels-inactieve
   * id blijven versturen en onherleidbaar met `CORE_TASK_INVALID_COMPETENCIES`
   * afgewezen worden. `editingCoreTask.competencies` (ongefilterd op
   * isActive, zie Story 2.1 `loadDetailsFor`) levert de naam voor weergave.
   */
  // Follow-up review fix: gate on `!isLoadingVakCompetencies` too — while
  // the active-competencies query is still in flight, `vakCompetencies` is
  // `undefined`/empty, which would otherwise make every legitimately-active
  // competency on the task momentarily render as "gearchiveerd" (false
  // positive, self-resolving once the query finishes, but risks the user
  // clicking "verwijderen" on a genuinely active competency in that window).
  const staleCompetencies =
    editingCoreTask && !isLoadingVakCompetencies
      ? formData.competencyIds
          .filter((id) => !(vakCompetencies ?? []).some((c) => c.id === id))
          .map((id) => editingCoreTask.competencies.find((c) => c.id === id))
          .filter((c): c is { id: number; name: string } => !!c)
      : [];

  const canSubmit =
    formData.name.trim().length > 0 &&
    !nameTakenWarning &&
    effectiveDomain.length > 0 &&
    trimmedSkills.length >= 1 &&
    formData.competencyIds.length >= 1 &&
    formData.competencyIds.length <= 3 &&
    staleCompetencies.length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Revalideer de naam-check op submit — een Enter-toets in het naamveld
    // kan de onBlur-check overslaan (epic-level review fix).
    const clash = findNameClash(formData.name);
    if (clash) {
      setNameTakenWarning(clash);
      setFormError(clash);
      return;
    }

    if (!canSubmit) {
      setFormError('Vul alle verplichte velden correct in.');
      return;
    }

    try {
      if (editingCoreTask) {
        const updateData: UpdateCoreTaskRequest = {
          name: formData.name.trim(),
          domain: effectiveDomain,
          skills: trimmedSkills,
          competencyIds: formData.competencyIds,
        };
        await updateCoreTask.mutateAsync({ id: editingCoreTask.id, data: updateData });
      } else {
        const createData: CreateCoreTaskRequest = {
          name: formData.name.trim(),
          domain: effectiveDomain,
          skills: trimmedSkills,
          competencyIds: formData.competencyIds,
        };
        await createCoreTask.mutateAsync(createData);
      }
      resetForm();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Er is een fout opgetreden bij het opslaan.';
      setFormError(message);
    }
  };

  const handleAddSkill = () => {
    setFormData((prev) => ({ ...prev, skills: [...prev.skills, ''] }));
  };

  const handleRemoveSkill = (index: number) => {
    setFormData((prev) => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));
  };

  const handleSkillChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.map((s, i) => (i === index ? value : s)),
    }));
  };

  const handleMoveSkill = (index: number, direction: -1 | 1) => {
    setFormData((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.skills.length) return prev;
      const next = [...prev.skills];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return { ...prev, skills: next };
    });
  };

  const handleToggleCompetency = (id: number, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      competencyIds: checked
        ? [...prev.competencyIds, id]
        : prev.competencyIds.filter((c) => c !== id),
    }));
  };

  /** Verwijdert een gearchiveerde competentie uit de selectie (zie staleCompetencies). */
  const handleRemoveStaleCompetency = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      competencyIds: prev.competencyIds.filter((c) => c !== id),
    }));
  };

  const handleArchiveRequest = (task: CoreTaskDTO) => {
    setArchiveError(null);
    setArchiveTarget(task);
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    setArchiveError(null);
    try {
      if (archiveTarget.isActive) {
        await archiveCoreTask.mutateAsync(archiveTarget.id);
      } else {
        await reactivateCoreTask.mutateAsync(archiveTarget.id);
      }
      setArchiveTarget(null);
    } catch (err) {
      // Epic-level review fix: laat de dialoog open zodat de gebruiker het
      // kan zien/opnieuw proberen — mét een zichtbare foutmelding i.p.v.
      // stilzwijgend niets te tonen.
      const message = err instanceof Error ? err.message : 'Er is een fout opgetreden.';
      setArchiveError(message);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="core-tasks-loading" className="animate-pulse space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="core-tasks-error" className="bg-[#D64545]/10 border border-[#D64545]/30 rounded-lg p-4">
        <p className="text-sm text-[#D64545]">Fout bij het laden van de kerntakencatalogus.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-[#1E293B]">Kerntakencatalogus</h2>
          <p className="text-sm text-gray-500">{activeCount} actief</p>
        </div>
        <button
          data-testid="add-core-task-button"
          onClick={handleAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2F5A7A] hover:bg-[#254a63]"
        >
          <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Kerntaak
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <input
          type="text"
          data-testid="core-tasks-search"
          placeholder="Zoeken op naam of domein..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            data-testid="toggle-show-archived"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="h-4 w-4 text-[#2F5A7A] focus:ring-[#54949E] border-gray-300 rounded"
          />
          Toon gearchiveerd
        </label>
      </div>

      {/* Table */}
      {groupedByDomain.length > 0 ? (
        // Bewust GEEN `overflow-hidden` hier (epic-level review fix): dat
        // clipte eerder de absoluut-gepositioneerde vaardigheden-popover en
        // het rij-acties-menu voor rijen onderin de tabel. De ronde hoeken
        // van de buitenste rand blijven zichtbaar via `rounded-lg`; de
        // enige visuele prijs is dat de allereerste/laatste tabelrand-pixel
        // theoretisch net buiten de afgeronde hoek kan uitsteken, wat in de
        // praktijk niet zichtbaar is bij een witte tabelachtergrond.
        <div className="border border-[#E2E8F0] rounded-lg">
          <table data-testid="core-tasks-table" className="min-w-full divide-y divide-[#E2E8F0]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kerntaak</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vaardigheden</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competenties</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">In gebruik</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acties</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E2E8F0]">
              {groupedByDomain.map(({ domain, tasks }) => (
                <FragmentGroup key={domain} domain={domain}>
                  {tasks.map((task) => (
                    <tr key={task.id} className={`hover:bg-gray-50 ${!task.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#1E293B]">{task.name}</span>
                          {!task.isActive && (
                            <span
                              data-testid="core-task-archived-chip"
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                            >
                              Gearchiveerd
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <SkillsPopover skills={task.skills} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {task.competencies.map((c) => (
                            <span
                              key={c.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-[#54949E] text-[#54949E]"
                            >
                              {c.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{task.inUseCount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <RowActionsMenu task={task} onEdit={handleEdit} onArchiveRequest={handleArchiveRequest} />
                      </td>
                    </tr>
                  ))}
                </FragmentGroup>
              ))}
            </tbody>
          </table>
        </div>
      ) : (coreTasks ?? []).length === 0 ? (
        <div data-testid="core-tasks-empty" className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-[#1E293B]">Nog geen kerntaken</h3>
          <p className="mt-2 text-sm text-gray-500">
            Begin met de eerste of vul de catalogus met de besloten kerntaken.
          </p>
        </div>
      ) : (
        // Epic-level review fix: onderscheid "catalogus echt leeg" (boven)
        // van "zoekopdracht levert niets op" (hier) — anders is de
        // "begin met de eerste kerntaak"-tekst misleidend na een zoekactie.
        <div data-testid="core-tasks-no-results" className="text-center py-12">
          <h3 className="text-lg font-medium text-[#1E293B]">Geen kerntaken gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">Pas de zoekopdracht of het filter aan.</p>
        </div>
      )}

      {/* Sheet: create/edit */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="core-task-sheet-title">
          <div className="absolute inset-0 bg-gray-500/75" onClick={resetForm} />
          <div className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-xl overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6">
              <h3 id="core-task-sheet-title" className="text-lg font-medium text-[#1E293B] mb-4">
                {editingCoreTask ? 'Kerntaak bewerken' : 'Nieuwe kerntaak'}
              </h3>

              {formError && (
                <div data-testid="core-task-form-error" className="mb-4 rounded-md bg-[#D64545]/10 border border-[#D64545]/30 px-3 py-2 text-sm text-[#D64545]">
                  {formError}
                </div>
              )}

              <div className="space-y-5">
                {/* Naam */}
                <div>
                  <label htmlFor="core-task-name" className="block text-sm font-medium text-gray-700">
                    Naam
                  </label>
                  <input
                    type="text"
                    id="core-task-name"
                    data-testid="core-task-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={(e) => checkNameTaken(e.target.value)}
                    maxLength={255}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                    required
                  />
                  {nameTakenWarning && (
                    <p data-testid="core-task-name-taken-warning" className="mt-1 text-xs text-[#D64545]">
                      {nameTakenWarning}
                    </p>
                  )}
                </div>

                {/* Domein */}
                <div>
                  <label htmlFor="core-task-domain" className="block text-sm font-medium text-gray-700">
                    Domein
                  </label>
                  <select
                    id="core-task-domain"
                    data-testid="core-task-domain-select"
                    value={formData.isNewDomain ? NEW_DOMAIN_SENTINEL : formData.domain}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        isNewDomain: value === NEW_DOMAIN_SENTINEL,
                        domain: value === NEW_DOMAIN_SENTINEL ? '' : value,
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                    required
                  >
                    <option value="">Kies een domein</option>
                    {domains.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                    <option value={NEW_DOMAIN_SENTINEL}>Nieuw domein…</option>
                  </select>
                  {formData.isNewDomain && (
                    <input
                      type="text"
                      data-testid="core-task-new-domain-input"
                      placeholder="Naam van het nieuwe domein"
                      value={formData.newDomain}
                      onChange={(e) => setFormData({ ...formData, newDomain: e.target.value })}
                      maxLength={255}
                      className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                      required
                    />
                  )}
                </div>

                {/* Vaardigheden */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vaardigheden</label>
                  <p className="text-xs text-gray-500 mb-2">Deze verschijnen als ankers bij het scoren.</p>
                  <div className="space-y-2">
                    {formData.skills.map((skill, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          data-testid={`core-task-skill-input-${index}`}
                          value={skill}
                          onChange={(e) => handleSkillChange(index, e.target.value)}
                          maxLength={255}
                          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleMoveSkill(index, -1)}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          aria-label="Omhoog"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveSkill(index, 1)}
                          disabled={index === formData.skills.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          aria-label="Omlaag"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          data-testid={`core-task-skill-remove-${index}`}
                          onClick={() => handleRemoveSkill(index)}
                          className="text-gray-400 hover:text-[#D64545]"
                          aria-label="Verwijderen"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid="core-task-add-skill-button"
                    onClick={handleAddSkill}
                    className="mt-2 text-sm text-[#54949E] hover:text-[#2F5A7A]"
                  >
                    + Vaardigheid toevoegen
                  </button>
                </div>

                {/* Vak-competenties */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Vak-competenties{' '}
                    <span data-testid="core-task-competency-counter" className="text-gray-400 font-normal">
                      ({formData.competencyIds.length}/3)
                    </span>
                  </label>
                  {formData.competencyIds.length > 3 && (
                    <p data-testid="core-task-too-many-competencies-warning" className="mt-1 text-xs text-[#D64545]">
                      Verwijder er minstens {formData.competencyIds.length - 3} om op te slaan (max. 3).
                    </p>
                  )}
                  {staleCompetencies.length > 0 && (
                    <div data-testid="core-task-stale-competencies-warning" className="mt-1 mb-2">
                      <p className="text-xs text-[#D64545]">
                        Deze competentie(s) zijn gearchiveerd — verwijder ze om op te slaan:
                      </p>
                      <ul className="mt-1 space-y-1">
                        {staleCompetencies.map((c) => (
                          <li key={c.id} className="flex items-center justify-between text-sm bg-[#D64545]/10 border border-[#D64545]/30 rounded px-2 py-1">
                            <span className="text-[#1E293B]">{c.name} (gearchiveerd)</span>
                            <button
                              type="button"
                              data-testid={`core-task-remove-stale-competency-${c.id}`}
                              onClick={() => handleRemoveStaleCompetency(c.id)}
                              className="text-gray-400 hover:text-[#D64545] ml-2"
                              aria-label={`Verwijder ${c.name}`}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isLoadingVakCompetencies ? (
                    <p className="text-sm text-gray-400 italic mt-1">Vak-competenties laden...</p>
                  ) : vakCompetenciesError ? (
                    <p data-testid="core-task-competencies-load-error" className="text-sm text-[#D64545] mt-1">
                      Kon vak-competenties niet laden.
                    </p>
                  ) : vakCompetencies && vakCompetencies.length > 0 ? (
                    <div className="mt-1 border border-gray-300 rounded-md max-h-40 overflow-y-auto">
                      {vakCompetencies.map((comp) => {
                        const checked = formData.competencyIds.includes(comp.id);
                        const disabled = !checked && formData.competencyIds.length >= 3;
                        return (
                          <label
                            key={comp.id}
                            className={`flex items-center px-3 py-2 border-b border-gray-100 last:border-b-0 ${
                              disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
                            }`}
                          >
                            <input
                              type="checkbox"
                              data-testid={`core-task-competency-checkbox-${comp.id}`}
                              checked={checked}
                              disabled={disabled}
                              onChange={(e) => handleToggleCompetency(comp.id, e.target.checked)}
                              className="h-4 w-4 text-[#2F5A7A] focus:ring-[#54949E] border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">{comp.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Geen vak-competenties beschikbaar.</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  data-testid="core-task-submit-button"
                  disabled={!canSubmit || createCoreTask.isPending || updateCoreTask.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2F5A7A] hover:bg-[#254a63] disabled:opacity-50"
                >
                  {createCoreTask.isPending || updateCoreTask.isPending ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive/reactivate confirmation dialog */}
      {archiveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-gray-500/75"
            onClick={() => {
              setArchiveTarget(null);
              setArchiveError(null);
            }}
          />
          <div className="relative z-10 bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-[#1E293B] mb-2">
              {archiveTarget.isActive ? 'Kerntaak archiveren' : 'Kerntaak dearchiveren'}
            </h3>
            <p data-testid="core-task-archive-impact-text" className="text-sm text-gray-600 mb-4">
              {archiveTarget.isActive
                ? archiveTarget.inUseCount > 0
                  ? `${archiveTarget.inUseCount} medewerker(s) hebben deze kerntaak in hun actieve pakket — bestaande toewijzingen blijven geldig, nieuwe zijn niet mogelijk.`
                  : 'Deze kerntaak heeft nog geen toewijzingen — u kunt hem archiveren.'
                : 'Deze kerntaak wordt weer beschikbaar voor nieuwe toewijzingen.'}
            </p>
            {archiveError && (
              <div data-testid="core-task-archive-error" className="mb-4 rounded-md bg-[#D64545]/10 border border-[#D64545]/30 px-3 py-2 text-sm text-[#D64545]">
                {archiveError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setArchiveTarget(null);
                  setArchiveError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                type="button"
                data-testid="core-task-archive-confirm-button"
                onClick={handleArchiveConfirm}
                disabled={archiveCoreTask.isPending || reactivateCoreTask.isPending}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2F5A7A] hover:bg-[#254a63] disabled:opacity-50"
              >
                {archiveTarget.isActive ? 'Archiveren' : 'Dearchiveren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Groepeert een domein als een grijze subkop-rij + de kerntaken-rijen erna,
 * binnen dezelfde `<tbody>` (React Fragment i.p.v. een nieuwe `<tbody>` per
 * groep — een `<table>` mag meerdere `<tbody>`'s hebben, maar één blijft
 * eenvoudiger voor styling/borders).
 */
function FragmentGroup({ domain, children }: { domain: string; children: React.ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={5} className="px-6 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {domain}
        </td>
      </tr>
      {children}
    </>
  );
}
